import {
  ApiError,
  apiDownload,
  apiRequest,
  apiRequestWithEtag,
  type IDownload,
  type IEtagged
} from '@/domains/api';
import type { IThreshold } from './metrics-updates-contracts';
import type { IPage } from './oversight-contracts';
import type {
  IExportColumn,
  IExportCondition,
  IExportCycle,
  IExportCycles,
  IExportPopulation,
  IExportTemplate,
  IFinalistFirm,
  IFinalistReport,
  IRankingFreezeChanges,
  IInsertPlacementBody,
  IMovePlacementBody,
  IRankingEdit,
  IRankingEdition,
  IRankingImportOptions,
  IRankingImportReport,
  IRankingJournalEvent,
  IRankingKey,
  IRankingOverview,
  IRankingTerritoryPlacement,
  ISegmentRule,
  ITerritoryEntry,
  ITerritoryMappingPreview,
  ITerritoryVocabulary
} from './ranking-process-contracts';
import { isObject, segment } from './rankings-api';

/**
 * survey-api's ranking process (SR-231, `RankingProcessEndpoints.cs`) — the sole reader of
 * `/api/v1/admin/rankings*`: the editions, one ranking with its `ETag`, a territory's placements,
 * the journal, the scored-result import and the three renumbering edits.
 *
 * **The `ETag` is opaque here**, the client's rule: one ranking's read hands it back, each edit
 * quotes it as `If-Match` and answers the next. Which of two answers is newer is told by the
 * body's `version`, never by parsing the header.
 *
 * **The import's body is the file itself**, as the photo's is, with its type named by the file's
 * extension where the browser names none (a CSV picked on Windows often has `type` ""): survey-api
 * reads `Content-Type` alone and answers 415 for anything but CSV or XLSX — its to judge.
 *
 * **A 2xx without the member a screen draws is a failed read** (`rankings-api.ts`' rule): the
 * screen holds the answer over its tables, and this app has no error boundary. A 404 on the one
 * ranking's read answers `null` — no ranking at the address yet, which the import creates.
 *
 * Each segment of an address is encoded as one, and `.`/`..` are refused unsent
 * (`isSendableSegment`) — a year typed on the page could otherwise reach another route.
 */

const BASE = '/api/v1/admin/rankings';

/** The journal is a tab's list, twenty a page. */
const JOURNAL_PAGE_SIZE = 20;

export const CSV_TYPE = 'text/csv';
export const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function rankingPath({ surveyCode, list, year }: IRankingKey): string {
  return [BASE, ...[surveyCode, list, String(year)].map(segment)].join('/');
}

function overviewOf(answer: IEtagged<unknown>): IEtagged<IRankingOverview> {
  member(answer.body, 'ranking', 'ranking');
  arrayMember(answer.body, 'territories', 'territories');
  return answer as IEtagged<IRankingOverview>;
}

function listPath(surveyCode: string, list: string): string {
  return `${BASE}/${segment(surveyCode)}/${segment(list)}`;
}

function vocabularyOf(answer: IEtagged<unknown>): IEtagged<ITerritoryVocabulary> {
  arrayMember(answer.body, 'territories', 'territories');
  arrayMember(answer.body, 'published', 'territories');
  return answer as IEtagged<ITerritoryVocabulary>;
}

function templatePath(surveyCode: string, list: string): string {
  return `${listPath(surveyCode, list)}/export-template`;
}

function templateOf(answer: IEtagged<unknown>): IEtagged<IExportTemplate> {
  arrayMember(answer.body, 'filter', 'template');
  arrayMember(answer.body, 'columns', 'template');
  return answer as IEtagged<IExportTemplate>;
}

function member<T>(answer: unknown, name: string, what: string): T {
  if (!isObject(answer) || !(name in answer)) {
    throw new Error(`The answer held no ${what}`);
  }
  return answer[name] as T;
}

function arrayMember<T>(answer: unknown, name: string, what: string): T[] {
  const value = member<unknown>(answer, name, what);
  if (!Array.isArray(value)) throw new Error(`The answer held no ${what}`);
  return value as T[];
}

function editOf(answer: IEtagged<unknown>): IEtagged<IRankingEdit> {
  arrayMember(answer.body, 'shifted', 'renumbering');
  if (typeof member(answer.body, 'version', 'version') !== 'number') {
    throw new Error('The answer to the edit held no version');
  }
  return answer as IEtagged<IRankingEdit>;
}

/** The type the import declares: the file's own, else its extension's. */
export function spreadsheetType(file: File): string {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) return CSV_TYPE;
  if (name.endsWith('.xlsx')) return XLSX_TYPE;
  return file.type || 'application/octet-stream';
}

/** The export's query: the cycle read, and the DD condition only where it is turned off. */
function exportQuery(cycle: Nullable<string>, dd: boolean): string {
  const query = new URLSearchParams();
  if (cycle) query.set('cycle', cycle);
  if (!dd) query.set('dd', 'false');
  const text = query.toString();
  return text ? `?${text}` : '';
}

function ifMatch(etag: string): Record<string, string> {
  return { 'If-Match': etag };
}

export const rankingProcessApi = {
  /** Every edition of a survey's lists (`internal_fields.read`). 400 `invalid-query` for a code
   *  that is no survey. */
  editions(surveyCode: string, signal?: AbortSignal): Promise<IRankingEdition[]> {
    return apiRequest<unknown>(`${BASE}?survey=${encodeURIComponent(surveyCode)}`, {
      signal
    }).then((answer) => arrayMember<IRankingEdition>(answer, 'rankings', 'editions'));
  },

  /** One ranking and its territories with the `ETag` an edit quotes; `null` for no ranking at
   *  the address (a bodiless 404 — also an unknown survey or a list outside its catalogue). */
  async overview(
    key: IRankingKey,
    signal?: AbortSignal
  ): Promise<Nullable<IEtagged<IRankingOverview>>> {
    try {
      return overviewOf(await apiRequestWithEtag<unknown>(rankingPath(key), { signal }));
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  /** One territory's placements — every placement of a national list with `territory` null. */
  placements(
    key: IRankingKey,
    territory: Nullable<string>,
    signal?: AbortSignal
  ): Promise<IRankingTerritoryPlacement[]> {
    const query = territory === null ? '' : `?territory=${encodeURIComponent(territory)}`;
    return apiRequest<unknown>(`${rankingPath(key)}/placements${query}`, {
      signal
    }).then((answer) =>
      arrayMember<IRankingTerritoryPlacement>(answer, 'placements', 'placements')
    );
  },

  /** Every flagged placement of the ranking, whatever the territory (SR-506): by territory, then
   *  place. */
  flaggedPlacements(key: IRankingKey, signal?: AbortSignal): Promise<IRankingTerritoryPlacement[]> {
    return apiRequest<unknown>(`${rankingPath(key)}/placements?flagged=true`, {
      signal
    }).then((answer) =>
      arrayMember<IRankingTerritoryPlacement>(answer, 'placements', 'placements')
    );
  },

  /** A page of the ranking's journal, newest first. */
  journal(
    key: IRankingKey,
    page: number,
    signal?: AbortSignal
  ): Promise<IPage<IRankingJournalEvent>> {
    return apiRequest<unknown>(
      `${rankingPath(key)}/events?page=${page}&pageSize=${JOURNAL_PAGE_SIZE}`,
      { signal }
    ).then((answer) => {
      arrayMember(answer, 'items', 'events');
      return answer as IPage<IRankingJournalEvent>;
    });
  },

  /** The whole journal as an XLSX (SR-508, `internal_fields.read`), newest first, in the History
   *  tab's columns; a bodiless 404 for no ranking at the address. */
  journalFile(key: IRankingKey): Promise<IDownload> {
    return apiDownload(`${rankingPath(key)}/events/export`);
  },

  /**
   * The scored result (`rankings.manage`): 200 with the report whether or not it was applied —
   * `dryRun` only checks. Refusals: 400 `invalid-file` (`detail` says why), 409
   * `invalid-transition` with `reason` `ranking-published` or `ranking-changed`, 400
   * `invalid-query` at `cycle` (a cycle — the file's Cycle column's or `cycle` — that carries no
   * such survey, SR-415), 413
   * `file-too-large`, 415 `file-unsupported-type`, a bodiless 404 for an address no catalogue
   * holds.
   */
  importResult(
    key: IRankingKey,
    file: File,
    options: IRankingImportOptions
  ): Promise<IRankingImportReport> {
    const query = new URLSearchParams({
      fileName: file.name,
      dryRun: String(options.dryRun),
      compact: String(options.compact),
      acceptWarnings: String(options.acceptWarnings)
    });
    if (options.cycle) query.set('cycle', options.cycle);
    return apiRequest<unknown>(`${rankingPath(key)}/import?${query.toString()}`, {
      method: 'POST',
      body: file,
      headers: { 'Content-Type': spreadsheetType(file) }
    }).then((answer) => {
      arrayMember(answer, 'errors', 'report');
      arrayMember(answer, 'warnings', 'report');
      return answer as IRankingImportReport;
    });
  },

  /**
   * The three edits of a draft (`rankings.manage`), each under `If-Match`. Refusals: 428 with no
   * header, 409 `revision-conflict` (`currentRevision`) with a stale one, 400 `invalid-body`, 409
   * `invalid-transition` (`reason` `already-placed` or `ranking-published`), a bodiless 404 (no
   * ranking, or — on a move or a removal — a participant it does not place).
   */
  insert(key: IRankingKey, etag: string, body: IInsertPlacementBody) {
    return apiRequestWithEtag<unknown>(`${rankingPath(key)}/placements`, {
      method: 'POST',
      body: withoutEmpty(body),
      headers: ifMatch(etag)
    }).then(editOf);
  },

  move(key: IRankingKey, etag: string, participantId: string, body: IMovePlacementBody) {
    return apiRequestWithEtag<unknown>(`${rankingPath(key)}/placements/${segment(participantId)}`, {
      method: 'PUT',
      body: withoutEmpty(body),
      headers: ifMatch(etag)
    }).then(editOf);
  },

  /** A list's scoring-input template (`internal_fields.read`, RP §9.2) and its `ETag`; the
   *  default at version 0 where none was saved. A bodiless 404 for a list outside the catalogue. */
  template(surveyCode: string, list: string, signal?: AbortSignal) {
    return apiRequestWithEtag<unknown>(templatePath(surveyCode, list), {
      signal
    }).then(templateOf);
  },

  /** The template replaced whole (`rankings.manage`) under `If-Match`: 400 `invalid-body` naming
   *  each entry by position, 409 `revision-conflict`, 428 without the header. */
  saveTemplate(
    surveyCode: string,
    list: string,
    etag: string,
    body: {
      filter: IExportCondition[];
      columns: IExportColumn[];
      /** Omitted, the saved finalist columns are kept. */
      finalistColumns?: IExportColumn[];
    }
  ) {
    return apiRequestWithEtag<unknown>(templatePath(surveyCode, list), {
      method: 'PUT',
      body,
      headers: ifMatch(etag)
    }).then(templateOf);
  },

  /** The scoring input for an edition (`rankings.manage`, RP §9.3) as its XLSX, from one cycle
   *  (SR-415): the ranking's linked cycle, else `cycle`; its submitted answers, and its figures at
   *  the pin's as-of date as the ranking froze them. **The export of record, a `POST`** (SR-451):
   *  where the address holds no ranking it creates the edition — an empty draft frozen now and
   *  linked to the cycle read — and it freezes a draft not frozen yet; survey-api's `GET` writes
   *  the same file and nothing else, and is not this screen's. 400 `invalid-query` at `cycle`
   *  (none, one not pinning the survey, a pin with no as-of date) or `year`; 409
   *  `invalid-transition` `answer-metric-column`; 422 `export-too-large` for a file the import
   *  could not read back — each refusal freezing nothing. */
  exportInput(key: IRankingKey, cycle: Nullable<string>, dd = true): Promise<IDownload> {
    return apiDownload(`${rankingPath(key)}/export${exportQuery(cycle, dd)}`, { method: 'POST' });
  },

  /** Who the scoring input holds and who it leaves out, and why (SR-320, `internal_fields.read`):
   *  the file holds the eligible on the list whose figures came in as of the cycle's date and,
   *  unless `dd` is false, whose DD interview is done. The `POST`'s 400s at `cycle`. */
  exportPopulation(
    key: IRankingKey,
    cycle: Nullable<string>,
    dd: boolean,
    signal?: AbortSignal
  ): Promise<IExportPopulation> {
    return apiRequest<unknown>(`${rankingPath(key)}/export/population${exportQuery(cycle, dd)}`, {
      signal
    }).then((answer) => {
      if (!isObject(answer) || typeof answer.included !== 'number') {
        throw new Error('The answer held no population');
      }
      return answer as unknown as IExportPopulation;
    });
  },

  /** Every cycle pinning the survey, newest first, with each pin's as-of date (SR-451,
   *  `internal_fields.read`) — what an edition is made on, and what the editions are grouped by. */
  surveyCycles(surveyCode: string, signal?: AbortSignal): Promise<IExportCycle[]> {
    return apiRequest<unknown>(
      `/api/v1/admin/rankings/cycles?survey=${encodeURIComponent(surveyCode)}`,
      { signal }
    ).then((answer) => arrayMember<IExportCycle>(answer, 'cycles', 'cycles'));
  },

  /** An edition made on its cycle (SR-451, `rankings.manage`), fixed from then on — or the one
   *  already kept at the address, answered as it is; 200 with the one-ranking read and its
   *  `ETag`. 400 `invalid-body` at `cycleCode` for a code no cycle carrying the survey has; 409
   *  `invalid-transition` `ranking-exists` where the edition kept there is on another cycle. */
  createEdition(key: IRankingKey, cycleCode: string) {
    return apiRequestWithEtag<unknown>(rankingPath(key), {
      method: 'POST',
      body: { cycleCode }
    }).then(overviewOf);
  },

  /** The cycles the scoring input can read, with each pin's as-of date (`internal_fields.read`). */
  exportCycles(key: IRankingKey, signal?: AbortSignal): Promise<IExportCycles> {
    return apiRequest<unknown>(`${rankingPath(key)}/export/cycles`, { signal }).then((answer) => {
      const cycles = arrayMember<IExportCycle>(answer, 'cycles', 'cycles');
      // `arrayMember` has thrown unless the answer is an object.
      const { linked } = answer as Record<string, unknown>;
      return { linked: typeof linked === 'string' ? linked : null, cycles };
    });
  },

  /** A draft published (RP §10.1) under `If-Match`, gaps closed first with `compact`; 200 with
   *  the one-ranking read and its `ETag`. 409 `invalid-transition` `ranking-not-contiguous` names
   *  the territories (`territories`, null for a national list's), `ranking-published`; 409
   *  `revision-conflict`; 428. */
  publish(key: IRankingKey, etag: string, compact: boolean) {
    return apiRequestWithEtag<unknown>(`${rankingPath(key)}/publish?compact=${compact}`, {
      method: 'POST',
      headers: ifMatch(etag)
    }).then(overviewOf);
  },

  /** A published ranking back to a draft (RP §10.3) under `If-Match`, the reason optional (blank
   *  left out); 200 with the one-ranking read and its `ETag`. 409 `invalid-transition`
   *  `ranking-draft` for a draft; 409 `revision-conflict`; 428. */
  returnToDraft(key: IRankingKey, etag: string, reason: string) {
    return apiRequestWithEtag<unknown>(`${rankingPath(key)}/draft`, {
      method: 'POST',
      body: reason.trim() === '' ? {} : { reason: reason.trim() },
      headers: ifMatch(etag)
    }).then(overviewOf);
  },

  /** What a refreeze to `to` (null for now) would change (SR-451, `internal_fields.read`): the
   *  figures that moved between the freeze and it. A 2xx without `changes` is a failed read. */
  freezeChanges(
    key: IRankingKey,
    to: Nullable<string>,
    signal?: AbortSignal
  ): Promise<IRankingFreezeChanges> {
    const query = to ? `?to=${encodeURIComponent(to)}` : '';
    return apiRequest<unknown>(`${rankingPath(key)}/freeze-changes${query}`, { signal }).then((answer) => {
      arrayMember(member<unknown>(answer, 'changes', 'changes'), 'items', 'changes');
      return answer as IRankingFreezeChanges;
    });
  },

  /** A draft's figures freeze moved to `to` — a moment between the freeze held and now — or, with
   *  null, to now (RP §10.4, SR-451, `figures.freeze`) under `If-Match`; 400 at `to` for a moment
   *  in the future;
   *  200 with the one-ranking read and its `ETag`. 409 `invalid-transition` `ranking-published`,
   *  or `freeze-backwards` where survey-api's clock is not past the freeze held; 409
   *  `revision-conflict`; 428. */
  refreeze(key: IRankingKey, etag: string, to: Nullable<string>) {
    return apiRequestWithEtag<unknown>(`${rankingPath(key)}/refreeze`, {
      method: 'POST',
      body: to ? { to } : {},
      headers: ifMatch(etag)
    }).then(overviewOf);
  },

  /** A draft's survey cycle (RP §10.2) set (`PUT`) or, with null, unlinked (`DELETE`), under
   *  `If-Match`; 200 with the one-ranking read. 400 `invalid-body` for a code that is no cycle. */
  linkCycle(key: IRankingKey, etag: string, cycleCode: Nullable<string>) {
    const path = `${rankingPath(key)}/cycle`;
    const sent =
      cycleCode === null
        ? apiRequestWithEtag<unknown>(path, { method: 'DELETE', headers: ifMatch(etag) })
        : apiRequestWithEtag<unknown>(path, {
            method: 'PUT',
            body: { cycleCode },
            headers: ifMatch(etag)
          });
    return sent.then(overviewOf);
  },

  /** An edition's threshold for the Metrics updates lists (SR-315, `rankings.manage`) set
   *  (`PUT`) or, with null, cleared (`DELETE`), under `If-Match`, on a draft or a published
   *  edition; 200 with the one-ranking read. 400 `problems[]` at the condition's member — a series
   *  the survey's figures do not hold, a minimum not above 0, a margin outside 0–99. */
  setThreshold(key: IRankingKey, etag: string, threshold: Nullable<IThreshold>) {
    const path = `${rankingPath(key)}/threshold`;
    const sent =
      threshold === null
        ? apiRequestWithEtag<unknown>(path, { method: 'DELETE', headers: ifMatch(etag) })
        : apiRequestWithEtag<unknown>(path, { method: 'PUT', body: threshold, headers: ifMatch(etag) });
    return sent.then(overviewOf);
  },

  /** The finalist files of `firms` (RP §11.1, `rankings.manage`) — one XLSX for one firm, else a
   *  ZIP of one per firm, or with `combine` one XLSX of them all (SR-505); `firms` null for every
   *  firm. `columns` null for the list's saved finalist columns. 400 `invalid-query` at
   *  `firms[i]`, `columns[i]…` or `layout`; a bodiless 404 where none of the firms holds a
   *  finalist. */
  finalistFiles(
    key: IRankingKey,
    firms: Nullable<string[]>,
    columns: Nullable<IExportColumn[]>,
    combine = false
  ): Promise<IDownload> {
    return apiDownload(`${rankingPath(key)}/finalists/files`, {
      method: 'POST',
      body: { firms, columns, combine }
    });
  },

  /** The ranking's firms and how many finalists each holds, ordered by name (RP §11.1). */
  finalistFirms(key: IRankingKey, signal?: AbortSignal): Promise<IFinalistFirm[]> {
    return apiRequest<unknown>(`${rankingPath(key)}/finalists/firms`, { signal }).then((answer) =>
      arrayMember<IFinalistFirm>(answer, 'firms', 'firms')
    );
  },

  /** A territorial list's vocabulary (`internal_fields.read`, territories design §3) and its
   *  `ETag`; `null` for a bodiless 404 — a national list, or one outside the catalogue. */
  async vocabulary(
    surveyCode: string,
    list: string,
    signal?: AbortSignal
  ): Promise<Nullable<IEtagged<ITerritoryVocabulary>>> {
    try {
      return vocabularyOf(
        await apiRequestWithEtag<unknown>(`${listPath(surveyCode, list)}/territories`, { signal })
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  /** The vocabulary replaced whole (`rankings.manage`) under `If-Match` — a name left out is
   *  retired, the body's order is the order. 400 `invalid-body` by position; 409
   *  `revision-conflict`; 428. */
  saveVocabulary(
    surveyCode: string,
    list: string,
    etag: string,
    territories: ITerritoryEntry[],
    segments: ISegmentRule
  ) {
    return apiRequestWithEtag<unknown>(`${listPath(surveyCode, list)}/territories`, {
      method: 'PUT',
      body: { segments, territories },
      headers: ifMatch(etag)
    }).then(vocabularyOf);
  },

  /** The vocabulary as a mapping file (`internal_fields.read`, SR-319) — one row per rule, columns
   *  Territory, Segment, State, City, Zip. */
  mappingFile(surveyCode: string, list: string, format: 'xlsx' | 'csv'): Promise<IDownload> {
    return apiDownload(`${listPath(surveyCode, list)}/territories/mapping?format=${format}`);
  },

  /** What a mapping file would make the vocabulary (`rankings.manage`, SR-319) — written nowhere;
   *  the body is the file, as the import's. 400 `invalid-file` (`detail` says why), 413, 415. */
  previewMapping(
    surveyCode: string,
    list: string,
    file: File
  ): Promise<ITerritoryMappingPreview> {
    return apiRequest<unknown>(`${listPath(surveyCode, list)}/territories/mapping`, {
      method: 'POST',
      body: file,
      headers: { 'Content-Type': spreadsheetType(file) }
    }).then((answer) => {
      arrayMember(answer, 'territories', 'mapping');
      arrayMember(answer, 'problems', 'mapping');
      return answer as ITerritoryMappingPreview;
    });
  },

  /** A firm's returned file (RP §11.2) — `firms` several for one file of several (SR-505): checked
   *  with no `If-Match` (`apply` null); applied with the ranking's `ETag`, answering the next one
   *  (and `version`). 400 `invalid-file`, 409 `revision-conflict` or `invalid-transition`
   *  (`ranking-published`), 413, 415, 428 when applied without `If-Match`. */
  importFinalists(
    key: IRankingKey,
    firms: string[],
    file: File,
    apply: Nullable<{ etag: string }>
  ): Promise<IEtagged<IFinalistReport>> {
    const query = new URLSearchParams([
      ...firms.map((firm) => ['firm', firm]),
      ['fileName', file.name],
      ['dryRun', String(apply === null)]
    ]);
    return apiRequestWithEtag<unknown>(
      `${rankingPath(key)}/finalists/import?${query.toString()}`,
      {
        method: 'POST',
        body: file,
        headers: { 'Content-Type': spreadsheetType(file), ...(apply ? ifMatch(apply.etag) : {}) }
      }
    ).then((answer) => {
      arrayMember(answer.body, 'errors', 'report');
      arrayMember(answer.body, 'removals', 'report');
      return answer as IEtagged<IFinalistReport>;
    });
  },

  remove(key: IRankingKey, etag: string, participantId: string, reason: string) {
    return apiRequestWithEtag<unknown>(
      `${rankingPath(key)}/placements/${segment(participantId)}?reason=${encodeURIComponent(reason)}`,
      { method: 'DELETE', headers: ifMatch(etag) }
    ).then(editOf);
  }
};

/** A body with its empty optional members left out. */
function withoutEmpty<T extends { territory?: string }>(body: T): T {
  const sent = { ...body };
  if (!sent.territory) delete sent.territory;
  return sent;
}
